import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMatch, useNavigate } from 'react-router-dom'
import { ArrowDown, Eye, MoreVertical, Phone, Plus, Power } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Permit } from '@/components/guards/Permit'
import { usePermissions } from '@/hooks/usePermissions'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ListPagination } from '@/components/shared/ListPagination'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import {
  ClientStatusTagBadge,
  ClientTypeTagBadge,
  ClientPaymentStatusBadge,
  ProspectBadge,
} from '@/components/shared/StatusBadges'
import { useClientsList, useClientsSummary } from '@/hooks/useClients'
import { useQuickActions } from '@/hooks/useQuickActions'
import { useUrlListParams } from '@/hooks/useUrlListParams'
import { useTopBarActions } from '@/contexts/TopBarActionsContext'
import { useAppSelector } from '@/store/hooks'
import { formatCurrency, formatInteger } from '@/lib/format'
import { primaryPhone } from '@/lib/clientContacts'
import { clientsApi } from '@/services/clients'
import { ClientMetricsSection } from '@/components/clients/ClientMetricsSection'
import { ClientListFilters } from '@/components/clients/ClientListFilters'
import { ListExportDialog } from '@/components/shared'
import {
  ClientFormDialog,
  type ClientFormTarget,
} from '@/components/clients/ClientFormDialog'
import { DeleteClientDialog } from '@/components/clients/DeleteClientDialog'
import { ToggleClientStatusDialog } from '@/components/clients/ToggleClientStatusDialog'
import whatsappLineIcon from '@/assets/clients/actions/whatsapp-line.svg'
import editProfileIcon from '@/assets/clients/actions/edit-profile.svg'
import trashIcon from '@/assets/clients/actions/trash.svg'
import { cn } from '@/lib/utils'
import type { Client, ClientListQuery, ClientType } from '@/types'

// Figma 2853:49768 — TopNavi primary CTA (New Customer).
const topBarNewClientClass =
  'h-10 gap-2 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-4 py-3 text-sm font-medium leading-none text-white hover:bg-[#e6511c]'

// Figma 2881:33987 — table chrome.
const thClass =
  'h-10 border-b border-[#e7e9ef] bg-[#fbfbfc] px-3 py-2 text-[10px] font-medium uppercase leading-[14px] tracking-normal text-[#3d434e]'

const tdClass =
  'h-16 overflow-hidden border-b border-[#e7e9ef] px-3 py-2 align-middle text-sm'

// Figma 6940:98305 — neutral chip; brand actions turn orange on hover.
const actionBtnClass =
  'inline-flex size-8 shrink-0 items-center justify-center rounded-lg border border-transparent bg-[#f4f5f8] p-1.5 text-[#272c36] transition-colors group-hover:bg-[#fbfbfc] hover:border-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f] group-hover:hover:bg-[#fff1ea]'
const whatsappActionBtnClass = cn(
  actionBtnClass,
  'hover:border-[#10b981] hover:bg-[#ecfdf5] hover:text-[#10b981] group-hover:hover:bg-[#ecfdf5]'
)
const actionMenuTriggerClass = cn(
  actionBtnClass,
  'data-[state=open]:border-[#ff5a1f] data-[state=open]:bg-[#fff1ea] data-[state=open]:text-[#ff5a1f] data-[state=open]:group-hover:bg-[#fff1ea] data-[state=open]:hover:bg-[#fff1ea]'
)

// Figma 2881:34146 / 2883:34653 — Edit Profile + Delete Profile only.
const clientMenuContentClass =
  'min-w-[200px] rounded-xl border border-[#e7e9ef] bg-white p-2 shadow-[0px_1px_4px_-1px_rgba(38,45,66,0.05),0px_1px_8px_-1px_rgba(38,45,66,0.1)]'
const clientMenuItemClass =
  'cursor-pointer gap-3 rounded-lg px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] focus:bg-[#f4f5f8] focus:text-[#171a20]'
const clientMenuItemDestructiveClass =
  'cursor-pointer gap-3 rounded-lg px-4 py-3 text-sm font-normal leading-[1.4] text-[#d63b33] focus:bg-[#f4f5f8] focus:text-[#d63b33]'

/** Figma 2881:33987 column widths (9 columns). */
const COL = {
  customer: 'w-[236px] min-w-[236px] text-left',
  type: 'w-[120px] min-w-[120px] text-center',
  status: 'w-[120px] min-w-[120px] text-center',
  contracts: 'w-[80px] min-w-[80px] text-center',
  totalRevenue: 'w-[180px] min-w-[100px] text-center',
  outstanding: 'w-[180px] min-w-[100px] text-center',
  paymentStatus: 'w-[180px] min-w-[120px] text-center',
  source: 'w-[120px] min-w-[120px] text-left',
  actions: 'w-[176px] min-w-[176px] max-w-[176px] text-right',
} as const

const TABLE_MIN_WIDTH = 1312
const COLUMN_COUNT = 9
// KPI cards (Figma 2881:33957) are part of the phase-2 clickable metrics scope.
const SHOW_CLIENT_LIST_METRICS = false

function clientLocation(c: Client): string {
  return [c.postal_code, c.city].filter(Boolean).join(' ')
}

function typeLabelKey(type: ClientType | string | undefined): string {
  if (type === 'individual') return 'list.typeLabels.individual'
  if (type === 'property_management') return 'list.typeLabels.propertyManager'
  return 'list.typeLabels.business'
}

function outstandingDisplay(value: number | null | undefined): {
  text: string
  tone: 'danger' | 'muted'
} {
  if (value == null || value <= 0) {
    return { text: '--', tone: 'muted' }
  }
  return { text: formatCurrency(value, 'EUR', 2), tone: 'danger' }
}

function filterFlagEnabled(value: unknown): boolean {
  return value === true || value === '1' || value === 'true'
}

// 2.4.1 Client list — Figma 953:59568 / 2881:33987 composition.
export default function ClientsListPage() {
  const { t } = useTranslation('clients')
  const navigate = useNavigate()
  const createMatch = useMatch('/clients/new')
  const editMatch = useMatch('/clients/:id/edit')
  const { callClient, whatsappClient } = useQuickActions()
  const role = useAppSelector(s => s.auth.user?.role)
  const isAdmin = role === 'Admin'
  const { canEdit, canDelete } = usePermissions()
  const canEditClients = canEdit('clients')
  const canDeleteClients = isAdmin && canDelete('clients')
  const showRowMenu = canEditClients || canDeleteClients
  const actionsGroupClass = cn(
    'ml-auto flex items-center justify-end gap-2',
    showRowMenu ? 'w-[152px]' : 'w-[112px]'
  )

  // Deep links /clients/new and /clients/:id/edit render this list and open the
  // right-side form panel (URL is the source of truth for open state).
  const formTarget = useMemo((): ClientFormTarget | null => {
    if (createMatch) return { mode: 'create' }
    if (editMatch?.params.id) return { mode: 'edit', id: editMatch.params.id }
    return null
  }, [createMatch, editMatch])

  const {
    page,
    setPage,
    limit,
    setLimit,
    search,
    onSearchChange,
    filters,
    setFilters,
    query,
  } = useUrlListParams<Omit<ClientListQuery, 'page' | 'limit' | 'search'>>({})

  const { data, isLoading, isError } = useClientsList(query as ClientListQuery)
  const { data: summaryData, isLoading: summaryLoading } = useClientsSummary({
    enabled: SHOW_CLIENT_LIST_METRICS,
  })
  const [exportOpen, setExportOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<Client | null>(null)
  const [statusTarget, setStatusTarget] = useState<{
    client: Client
    action: 'activate' | 'deactivate'
  } | null>(null)

  const clients = data?.data ?? []

  // New Customer CTA lives in the shared TopBar (Figma 2853:49768).
  const newClientAction = useMemo(
    () => (
      <Permit module='clients' action='edit'>
        <Button
          className={topBarNewClientClass}
          onClick={() => navigate('/clients/new')}
        >
          <Plus className='size-4' />
          {t('list.newClient')}
        </Button>
      </Permit>
    ),
    [navigate, t]
  )
  useTopBarActions(newClientAction)

  const toggleSort = (column: string) => {
    const current = (filters as ClientListQuery).sort_column
    const order = (filters as ClientListQuery).sort_order ?? 'desc'
    setFilters({
      sort_column: column,
      sort_order: current === column && order === 'asc' ? 'desc' : 'asc',
    } as Partial<Omit<ClientListQuery, 'page' | 'limit' | 'search'>>)
  }

  const sortIconClass = (column: string) => {
    const current = (filters as ClientListQuery).sort_column
    const order = (filters as ClientListQuery).sort_order ?? 'desc'
    // Figma shows the Total Revenue arrow as the default sort cue even before
    // a sort_column is set (list defaults to created_at desc server-side).
    const active =
      current === column ||
      (column === 'total_revenue' && (current == null || current === ''))
    return cn(
      'h-4 w-4 shrink-0 transition-transform',
      active && order === 'asc' && 'rotate-180'
    )
  }

  // Tenant-wide KPI cards (Figma 2881:33957) from /clients/summary.
  const listMetrics = useMemo(() => {
    const s = summaryData?.data
    return {
      customers: s?.customers ?? null,
      businessCustomers: s?.business_customers ?? null,
      individualCustomers: s?.individual_customers ?? null,
      revenue: s?.revenue ?? null,
      billedCustomers: s?.billed_customers ?? null,
      toBeCashed: s?.to_be_cashed ?? null,
      toBeCashedCustomers: s?.to_be_cashed_customers ?? null,
      contractsFollowUp: s?.contracts_follow_up ?? null,
      contractsFollowUpCustomers: s?.contracts_follow_up_customers ?? null,
    }
  }, [summaryData?.data])
  const metricActions = useMemo(
    () => ({
      customers: () => navigate('/clients?status=active'),
      revenue: () => navigate('/clients?has_revenue=1'),
      toBeCashed: () => navigate('/clients?has_outstanding_balance=1'),
      contractsFollowUp: () => navigate('/clients?has_active_contracts=1'),
    }),
    [navigate]
  )
  const activeMetricKey = (() => {
    if (filters.status === 'active') return 'customers'
    if (filterFlagEnabled(filters.has_revenue)) return 'revenue'
    if (filterFlagEnabled(filters.has_outstanding_balance)) return 'toBeCashed'
    if (filterFlagEnabled(filters.has_active_contracts)) {
      return 'contractsFollowUp'
    }
    return undefined
  })()

  return (
    <div className='flex flex-col gap-6'>
      {SHOW_CLIENT_LIST_METRICS && (
        <ClientMetricsSection
          metrics={listMetrics}
          loading={summaryLoading}
          actions={metricActions}
          activeKey={activeMetricKey}
        />
      )}

      {/* Toolbar (Figma 2881:33979): Search → Export → Date → Type → Status → Source */}
      <ClientListFilters
        search={search}
        onSearchChange={onSearchChange}
        filters={filters}
        setFilters={setFilters}
        onExport={() => setExportOpen(true)}
      />

      <Card className='overflow-hidden rounded-2xl border border-[#e7e9ef] shadow-[0_1px_8px_-1px_rgba(38,45,66,0.1),0_1px_4px_-1px_rgba(38,45,66,0.05)]'>
        <div className='overflow-x-auto'>
          <table
            className='w-full table-fixed caption-bottom text-sm'
            style={{ minWidth: TABLE_MIN_WIDTH }}
          >
            <TableHeader className='sticky top-0 z-10 bg-[#fbfbfc]'>
              <TableRow className='border-0'>
                <TableHead className={cn(thClass, COL.customer)}>
                  {t('list.columns.customer')}
                </TableHead>
                <TableHead className={cn(thClass, COL.type)}>
                  {t('list.columns.type')}
                </TableHead>
                <TableHead className={cn(thClass, COL.status)}>
                  {t('list.columns.status')}
                </TableHead>
                <TableHead className={cn(thClass, COL.contracts)}>
                  {t('list.columns.contracts')}
                </TableHead>
                <TableHead className={cn(thClass, COL.totalRevenue)}>
                  <button
                    type='button'
                    className='inline-flex items-center justify-center gap-2 text-center uppercase'
                    onClick={() => toggleSort('total_revenue')}
                  >
                    {t('list.columns.totalRevenue')}
                    <ArrowDown className={sortIconClass('total_revenue')} />
                  </button>
                </TableHead>
                <TableHead className={cn(thClass, COL.outstanding)}>
                  {t('list.columns.outstanding')}
                </TableHead>
                <TableHead className={cn(thClass, COL.paymentStatus)}>
                  {t('list.columns.paymentStatus')}
                </TableHead>
                <TableHead className={cn(thClass, COL.source)}>
                  {t('list.columns.source')}
                </TableHead>
                <TableHead className={cn(thClass, COL.actions)}>
                  <div className={actionsGroupClass}>
                    {t('list.columns.actions')}
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={COLUMN_COUNT} className={tdClass}>
                      <Skeleton className='h-6 w-full' />
                    </TableCell>
                  </TableRow>
                ))}

              {isError && !isLoading && (
                <TableRow>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    className={cn(
                      tdClass,
                      'py-10 text-center text-destructive'
                    )}
                  >
                    {t('list.loadError')}
                  </TableCell>
                </TableRow>
              )}

              {!isLoading && !isError && clients.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    className={cn(
                      tdClass,
                      'py-10 text-center text-muted-foreground'
                    )}
                  >
                    {t('list.empty')}
                  </TableCell>
                </TableRow>
              )}

              {!isLoading &&
                !isError &&
                clients.map(c => {
                  const isActive = (c.status ?? 'active') === 'active'
                  const location = clientLocation(c)
                  const outstanding = outstandingDisplay(
                    c.outstanding_receivables
                  )
                  const callTooltip = t('list.actions.call', { name: c.name })
                  const whatsappTooltip = t('list.actions.whatsapp')
                  const viewTooltip = t('list.actions.view')
                  const moreActionsTooltip = t('list.actions.moreActions')
                  return (
                    <TableRow
                      key={c.id}
                      className='group relative cursor-pointer'
                      onClick={() => navigate(`/clients/${c.id}`)}
                    >
                      <TableCell className={cn(tdClass, COL.customer)}>
                        <span className='absolute inset-y-0 left-0 hidden w-1 bg-[#ff5a1f] group-hover:block' />
                        <div className='flex flex-col gap-0.5'>
                          <div className='flex min-w-0 items-center gap-1'>
                            <TruncatedTextTooltip
                              className='line-clamp-2 text-sm font-medium leading-[1.4] text-[#171a20] group-hover:text-[#ff5a1f]'
                              content={c.name}
                            >
                              {c.name}
                            </TruncatedTextTooltip>
                            <ProspectBadge category={c.category} />
                          </div>
                          <TruncatedTextTooltip
                            className='line-clamp-2 text-xs font-normal leading-4 text-[#3d434e]'
                            content={location || undefined}
                          >
                            {location || '—'}
                          </TruncatedTextTooltip>
                        </div>
                      </TableCell>
                      <TableCell className={cn(tdClass, COL.type)}>
                        <ClientTypeTagBadge
                          type={c.type}
                          label={t(typeLabelKey(c.type))}
                        />
                      </TableCell>
                      <TableCell className={cn(tdClass, COL.status)}>
                        <ClientStatusTagBadge
                          status={c.status ?? 'active'}
                          label={t(
                            `common:status.${(c.status ?? 'active').toLowerCase()}`,
                            {
                              defaultValue: c.status ?? 'active',
                            }
                          )}
                        />
                      </TableCell>
                      <TableCell className={cn(tdClass, COL.contracts)}>
                        <span className='text-sm leading-[1.4] text-[#171a20]'>
                          {formatInteger(c.contracts_count ?? 0)}
                        </span>
                      </TableCell>
                      <TableCell className={cn(tdClass, COL.totalRevenue)}>
                        <span className='text-sm font-medium leading-[1.4] text-[#171a20]'>
                          {formatCurrency(c.total_revenue, 'EUR', 2)}
                        </span>
                      </TableCell>
                      <TableCell className={cn(tdClass, COL.outstanding)}>
                        <span
                          className={cn(
                            'text-sm font-medium leading-[1.4]',
                            outstanding.tone === 'danger'
                              ? 'text-[#d63b33]'
                              : 'text-[#6c7285]'
                          )}
                        >
                          {outstanding.text}
                        </span>
                      </TableCell>
                      <TableCell className={cn(tdClass, COL.paymentStatus)}>
                        <ClientPaymentStatusBadge
                          status={c.payment_status ?? 'normal'}
                          label={t(
                            `list.paymentStatus.${(c.payment_status ?? 'normal').toLowerCase()}`,
                            {
                              defaultValue: c.payment_status ?? 'normal',
                            }
                          )}
                        />
                      </TableCell>
                      <TableCell className={cn(tdClass, COL.source)}>
                        <span className='block truncate text-sm leading-[1.4] text-[#171a20]'>
                          {c.source
                            ? t(`sourceLabels.${c.source.toLowerCase()}`, {
                                defaultValue: c.source,
                              })
                            : '—'}
                        </span>
                      </TableCell>
                      <TableCell
                        className={cn(tdClass, COL.actions)}
                        onClick={event => event.stopPropagation()}
                      >
                        <div className={actionsGroupClass}>
                          <TruncatedTextTooltip
                            alwaysShow
                            closeOnPointerDown
                            className='inline-flex shrink-0'
                            content={callTooltip}
                            delayDuration={0}
                          >
                            <button
                              type='button'
                              className={actionBtnClass}
                              aria-label={callTooltip}
                              onClick={() => callClient(primaryPhone(c))}
                            >
                              <Phone className='size-4' strokeWidth={1.75} />
                            </button>
                          </TruncatedTextTooltip>
                          <TruncatedTextTooltip
                            alwaysShow
                            closeOnPointerDown
                            className='inline-flex shrink-0'
                            content={whatsappTooltip}
                            delayDuration={0}
                          >
                            <button
                              type='button'
                              className={cn(
                                whatsappActionBtnClass,
                                'group/whatsapp'
                              )}
                              aria-label={whatsappTooltip}
                              onClick={() => whatsappClient(primaryPhone(c))}
                            >
                              <img
                                src={whatsappLineIcon}
                                alt=''
                                width={16}
                                height={16}
                                className='size-[16px] shrink-0 transition-[filter] group-hover/whatsapp:[filter:brightness(0)_saturate(100%)_invert(55%)_sepia(74%)_saturate(1027%)_hue-rotate(118deg)_brightness(94%)_contrast(87%)]'
                              />
                            </button>
                          </TruncatedTextTooltip>
                          <TruncatedTextTooltip
                            alwaysShow
                            closeOnPointerDown
                            className='inline-flex shrink-0'
                            content={viewTooltip}
                            delayDuration={0}
                          >
                            <button
                              type='button'
                              className={actionBtnClass}
                              aria-label={viewTooltip}
                              onClick={() => navigate(`/clients/${c.id}`)}
                            >
                              <Eye className='size-4' strokeWidth={1.75} />
                            </button>
                          </TruncatedTextTooltip>
                          {showRowMenu && (
                            <DropdownMenu>
                              <TruncatedTextTooltip
                                alwaysShow
                                closeOnPointerDown
                                className='inline-flex shrink-0'
                                content={moreActionsTooltip}
                                delayDuration={0}
                              >
                                <DropdownMenuTrigger asChild>
                                  <button
                                    type='button'
                                    className={actionMenuTriggerClass}
                                    aria-label={moreActionsTooltip}
                                  >
                                    <MoreVertical
                                      className='size-4'
                                      strokeWidth={1.75}
                                    />
                                  </button>
                                </DropdownMenuTrigger>
                              </TruncatedTextTooltip>
                              <DropdownMenuContent
                                align='end'
                                sideOffset={8}
                                className={clientMenuContentClass}
                              >
                                {canEditClients && (
                                  <>
                                    <DropdownMenuItem
                                      className={clientMenuItemClass}
                                      onClick={() =>
                                        navigate(`/clients/${c.id}/edit`)
                                      }
                                    >
                                      <img
                                        src={editProfileIcon}
                                        alt=''
                                        width={20}
                                        height={20}
                                        className='size-4 shrink-0'
                                      />
                                      {t('list.actions.editProfile')}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      className={clientMenuItemClass}
                                      onClick={() =>
                                        setStatusTarget({
                                          client: c,
                                          action: isActive
                                            ? 'deactivate'
                                            : 'activate',
                                        })
                                      }
                                    >
                                      <Power
                                        className='size-4 shrink-0'
                                        strokeWidth={1.75}
                                      />
                                      {isActive
                                        ? t('list.actions.deactivate')
                                        : t('list.actions.activate')}
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {canDeleteClients && (
                                  <DropdownMenuItem
                                    className={clientMenuItemDestructiveClass}
                                    onClick={() => setDeleteTarget(c)}
                                  >
                                    <img
                                      src={trashIcon}
                                      alt=''
                                      width={16}
                                      height={16}
                                      className='size-4 shrink-0'
                                    />
                                    {t('list.actions.deleteProfile')}
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
            </TableBody>
          </table>
        </div>

        <ListPagination
          className='shrink-0'
          metadata={data?.metadata}
          page={page}
          pageSize={limit}
          onPageChange={setPage}
          onPageSizeChange={setLimit}
          showShowingRange={false}
        />
      </Card>

      <ListExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        entityLabel='Client'
        title={t('export.title')}
        scopeNote={t('export.scopeNote')}
        rowCount={clients.length}
        onExport={async format => {
          const res = await clientsApi.export({
            ...(query as ClientListQuery),
            format,
          })
          return {
            url: res.data!.url,
            filename: res.data!.filename,
          }
        }}
      />

      <DeleteClientDialog
        client={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={() => setDeleteTarget(null)}
      />

      <ToggleClientStatusDialog
        client={statusTarget?.client ?? null}
        action={statusTarget?.action ?? 'deactivate'}
        onClose={() => setStatusTarget(null)}
        onDone={() => setStatusTarget(null)}
      />

      <ClientFormDialog
        target={formTarget}
        onClose={() => navigate('/clients', { replace: true })}
        onSuccess={() => {
          navigate('/clients', { replace: true })
        }}
      />
    </div>
  )
}
